
/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * Filter dropdown
 *
 * @file TableFilter.jsx
 * @author Anuja Gaikwad
 * @since 1.4.7
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Menu } from '@mui/material';

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

import TickIcon from '../icons/TickIcon';
import {
  FilterConatiner,
  FilterLabel,
  FilterSelect,
  FilterSelectContent,
  FilterSelectedText,
  FilterArrowIcon,
  TableFilterPopover,
  TableFilterMenuItem,
  FilterMenuItemContent,
  FilterMenuItemText,
  FilterMenuItemSelectedText,
  FilterCheckIcon
} from '../tabular/styles/TabularStyles';

const TableFilter = ({
  filterByText,
  filterConfig,
  value,
  onChange,
  components,
  filterSelectClassName
}) => {
  const [anchorEl, setAnchorEl] = useState(null);

  const FilterSelectComponent = components?.filterSelect || FilterSelect;

  const open = Boolean(anchorEl);

  const openDropdown = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const closeDropdown = () => {
    setAnchorEl(null);
  };

  const selectedOption =
    filterConfig?.options?.find(
      (option) => option.value === value
    ) || filterConfig?.options?.[0];

  return (
    <>
      <FilterConatiner>
        <FilterLabel>
          {filterByText}
        </FilterLabel>

        <FilterSelectComponent
          className={[open ? 'filterSelectOpen' : '', filterSelectClassName].filter(Boolean).join(' ')}
          onClick={openDropdown}
          disableRipple
          disableFocusRipple
          disableElevation
        >
          <FilterSelectContent>
            <FilterSelectedText>
              {selectedOption?.label}
            </FilterSelectedText>

            {open ? (
              <FilterArrowIcon>
                <ArrowDropUpIcon />
              </FilterArrowIcon>
            ) : (
              <FilterArrowIcon>
                <ArrowDropDownIcon />
              </FilterArrowIcon>
            )}
          </FilterSelectContent>
        </FilterSelectComponent>
      </FilterConatiner>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeDropdown}
        onClick={(event) => event.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        slotProps={{
          paper: {
            component: TableFilterPopover
          },
          list: {
            disablePadding: true
          }
        }}
      >
        {filterConfig.options.map((option) => (
          <TableFilterMenuItem
            key={option.id}
            selected={value === option.value}
            onClick={() => {
              onChange(option.value);
              closeDropdown();
            }}
          >
            <FilterMenuItemContent>
              {value === option.value ? (
                <FilterMenuItemSelectedText>
                  {option.label}
                </FilterMenuItemSelectedText>
              ) : (
                <FilterMenuItemText>
                  {option.label}
                </FilterMenuItemText>
              )}

              {value === option.value && (
                <FilterCheckIcon>
                  <TickIcon />
                </FilterCheckIcon>
              )}
            </FilterMenuItemContent>
          </TableFilterMenuItem>
        ))}
      </Menu>
    </>
  );
};

TableFilter.propTypes = {
  filterByText: PropTypes.string.isRequired,
  filterConfig: PropTypes.object.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  components: PropTypes.shape({
    filterSelect: PropTypes.elementType
  }),
  filterSelectClassName: PropTypes.string
};

TableFilter.defaultProps = {
  components: {},
  filterSelectClassName: ''
};

export default TableFilter;
